import {Component, Input} from '@angular/core';
import {Contact} from "../../model/contact";
import {ModalsService} from "../../services/modals.service";
import {RulesService} from "../../services/rules.service";
import {catchError, of} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {Rule} from "../../model/rule";
import {constants} from "../../constants";
import {MetrikaService} from "../../services/metrika.service";

@Component({
  selector: 'app-edit-contact-modal',
  templateUrl: './edit-contact-modal.component.html',
  styleUrls: ['./edit-contact-modal.component.css']
})
export class EditContactModalComponent {
  private _contact: Contact;
  newContact: boolean;
  showRules: boolean;
  rulesSaved: boolean;
  requestInProgress: boolean;

  title: string;
  msisdn: string;
  msisdnError: string;
  rules: Rule[];

  nextActive: boolean;

  titlePattern = constants.titlePattern;

  constructor(private readonly modalsService: ModalsService,
              private readonly rulesService: RulesService,
              private metrikaService: MetrikaService) {
    this._contact = null;
    this.newContact = true;
    this.showRules = false;
    this.rulesSaved = false;
    this.requestInProgress = false;

    this.title = '';
    this.msisdn = '';
    this.msisdnError = null;
    this.rules = [];

    this.nextActive = false;
  }

  get contact(): Contact {
    return this._contact;
  }

  @Input() set contact(value: Contact) {
    this._contact = value;
    this.newContact = !value;

    if (value) {
      this.title = value.title;
      this.msisdn = value.msisdn;
      this.rules = value.rules;
    }

    this.contactFieldChanged();
  }

  close(): void {
    this.modalsService.hideModal();
  }

  back(): void {
    if (this.showRules) {
      this.showRules = false;
    } else {
      this.modalsService.showContactInfo(this._contact);
    }
  }

  contactFieldChanged(): void {
    this.nextActive = this.title?.trim().length > 0 && this.msisdn?.length == 11;
  }

  msisdnChanged(): void {
    if (!this.msisdn.startsWith('7')) {
      this.msisdn = '7' + this.msisdn;
    }
    this.msisdnError = null;
    this.contactFieldChanged();
  }

  saveContact(): void {
    if (this.nextActive && !this.requestInProgress) {
      this.requestInProgress = true;
      if (this.newContact) {
        this.rulesService.createContact(new Contact(null, this.title.trim(), this.msisdn))
          .pipe(catchError((error: HttpErrorResponse) => {
            if (error.status == 409) {
              this.msisdnError = 'edit-contact-modal.contact-exist';
            } else {
              this.msisdnError = 'edit-contact-modal.unexpected-error';
            }
            return of(null);
          }))
          .subscribe(id => {
            if (id) {
              this.contact = new Contact(id, this.title.trim(), this.msisdn);
              this.showRules = true;
              this.metrikaService.addEvent('create_contact', null, 'SUCCESS')
            }
            this.requestInProgress = false;
          });
      } else {
        this.rulesService.editContact(new Contact(this.contact.id, this.title.trim(), this.msisdn))
          .subscribe(status => {
            if (status == 200) {
              this.contact.title = this.title.trim();
              this.contact.msisdn = this.msisdn;
              this.metrikaService.addEvent('edit_contact', null, 'success')
              this.showRules = true;
            } else if (status == 409) {
              this.msisdnError = 'edit-contact-modal.contact-exist';
            } else {
              this.msisdnError = 'edit-contact-modal.unexpected-error';
            }
            this.requestInProgress = false;
          });
      }
    }
  }

  saveRules(rulesToSave: Array<Rule>): void {
    if (!this.requestInProgress) {
      this.requestInProgress = true;
      this.rulesService.updateContactRules(this.contact.id, rulesToSave)
        .subscribe(code => {
          if (code == 200) {
            this.rulesSaved = true;
            this.rulesService.loadContacts();

            let groupContains = this.rulesService.groupsValue.map(group => group.id).includes(this.contact.id);
            if (groupContains) {
              this.metrikaService.addEvent('add_rule_for_group', null, 'SUCCESS')
            } else {
              this.metrikaService.addEvent('add_rule_for_contact', null, 'SUCCESS')
            }
          } else {
            console.error(`Cannot save contact rules, code: ${code}`);
          }
          this.requestInProgress = false;
        });
    }
  }

  deleteRules() {
    if (!this.requestInProgress) {
      this.requestInProgress = true;
      this.rulesService.deleteContactRules(this.contact.id)
        .subscribe(code => {
          if (code == 200) {
            this.rulesSaved = true;
            this.rulesService.loadContacts();
          } else {
            console.error(`Cannot save contact rules, code: ${code}`);
          }
          this.requestInProgress = false;
        });
    }
  }
}
